/**
 * useNotificationPreferences — React Query hook for per-user notification prefs.
 *
 * Backing endpoints: GET/PUT /api/v1/notification-preferences.
 * Backend derives identity from JWT; update payloads MUST NOT include user_id,
 * customer_id, created_at, or updated_at (the API client type enforces this).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationPrefsApi,
  type NotificationPreferences,
  type NotificationPreferencesUpdate,
} from '../services/notificationPrefsApi';

export const NOTIFICATION_PREFS_QUERY_KEY = ['notification-preferences'] as const;

export function useNotificationPreferences() {
  const qc = useQueryClient();

  const query = useQuery<NotificationPreferences>({
    queryKey: NOTIFICATION_PREFS_QUERY_KEY,
    queryFn: () => notificationPrefsApi.get(),
    staleTime: 60_000,
  });

  const mutation = useMutation<
    NotificationPreferences,
    Error,
    NotificationPreferencesUpdate
  >({
    mutationFn: (update) => notificationPrefsApi.update(update),
    onSuccess: (data) => {
      qc.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, data);
    },
  });

  return {
    preferences: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
